import { TranslocoService } from '@jsverse/transloco';

export type ExpenseStatusFilterTab = 'paid' | 'due' | 'overdue' | 'all';

// ── Enum maps (byte values from API) ────────────────────────────────
export const PAYMENT_MODE = { Later: 0, Paid: 1 } as const;
export const PAYMENT_STATE = { Pending: 0, Paid: 1, Overdue: 2, PartiallyPaid: 3 } as const;
export const PAYMENT_CHANNEL = { Bank: 0, CreditCard: 1, Cash: 2, Other: 3 } as const;
export const EXPENSE_STATUS = { Draft: 0, Approved: 1, Cancelled: 2 } as const;

export type PaymentModeValue = (typeof PAYMENT_MODE)[keyof typeof PAYMENT_MODE];
export type PaymentStateValue = (typeof PAYMENT_STATE)[keyof typeof PAYMENT_STATE];
export type PaymentChannelValue = (typeof PAYMENT_CHANNEL)[keyof typeof PAYMENT_CHANNEL];
export type ExpenseStatusValue = (typeof EXPENSE_STATUS)[keyof typeof EXPENSE_STATUS];

// ── Reverse-lookup helpers ──────────────────────────────────────────
const paymentModeLabels: Record<PaymentModeValue, string> = { 0: 'Sonra', 1: 'Ödendi' };
const paymentStateLabels: Record<PaymentStateValue, string> = { 0: 'Odenecek', 1: 'Odenmis', 2: 'Gecikmis', 3: 'Kismi Odeme' };
const paymentChannelLabels: Record<PaymentChannelValue, string> = { 0: 'Banka', 1: 'Kredi Karti', 2: 'Nakit', 3: 'Diger' };
const expenseStatusLabels: Record<ExpenseStatusValue, string> = { 0: 'Taslak', 1: 'Onaylanmis', 2: 'Iptal' };

export function paymentModeLabel(v: number): string { return paymentModeLabels[v as PaymentModeValue] ?? `${v}`; }
export function paymentStateLabel(v: number): string { return paymentStateLabels[v as PaymentStateValue] ?? `${v}`; }
export function paymentChannelLabel(v: number): string { return paymentChannelLabels[v as PaymentChannelValue] ?? `${v}`; }
export function expenseStatusLabel(v: number): string { return expenseStatusLabels[v as ExpenseStatusValue] ?? `${v}`; }

// ── DTOs ────────────────────────────────────────────────────────────
export interface ExpenseListItemDto {
  publicId: string;
  id: string;
  expenseCategoryPublicId: string;
  expenseCategoryName: string;
  categoryGroupName: string;
  categoryPath: string;
  transactionDate: string;
  documentNo: string;
  dueDate?: string;
  projectName?: string;
  accountName: string;
  accountPublicId?: string;
  amountGross: number;
  vatRate: number;
  netAmount: number;
  currencyId: number;
  currencyCode: string;
  exchangeRate?: number | null;
  /** KDV istisnası (ör. UK zero-rated); seçildiğinde VAT 0'a zorlanır. */
  vatExemptionTypeId?: number | null;
  exemptionCode?: string | null;
  exemptionReason?: string | null;
  /** Ödeme takibi (kısmi ödeme desteği). */
  amountPaid?: number | null;
  amountDue?: number | null;
  lastPaymentDate?: string | null;
  paymentMode: string;
  paymentState: string;
  paymentChannel: string;
  status: string;
  recurringEnabled: boolean;
  hasAttachment: boolean;
  attachmentName?: string;
  description?: string;
  note?: string;
  source:string;
  paymentDate?: string;
  isActive: boolean;
  /** İptal edilen harcamalar eşleştirme listelerinde gösterilmez. */
  isCancelled: boolean;
}

export interface ExpenseDetailDto extends ExpenseListItemDto {}

// ── Filter & Response ───────────────────────────────────────────────
export interface ExpenseListFilter {
  page?: number;
  pageSize?: number;
  status?: number;
  paymentState?: number;
  transactionDateFrom?: string;
  transactionDateTo?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  expenseCategoryPublicId?: string | null;
}

export interface ExpenseListResponse {
  items: ExpenseListItemDto[];
  totalCount: number;
  page: number;
  pageSize: number;
}

// ── Create / Update requests ────────────────────────────────────────
export interface CreateExpenseRequest {
  expenseCategoryPublicId: string;
  transactionDate: string;
  accountName: string;
  amountGross: number;
  vatRate: number;
  netAmount: number;
  currencyId: number;
  exchangeRate?: number | null;
  vatExemptionTypeId?: number | null;
  paymentMode: number;
  paymentState: number;
  paymentChannel: number;
  status: number;
  recurringEnabled: boolean;
  documentNo?: string;
  dueDate?: string;
  paymentDate?: string;
  projectName?: string;
  accountPublicId?: string;
  description?: string;
  note?: string;
}

export interface UpdateExpenseRequest extends CreateExpenseRequest {
  isActive: boolean;
}

// ── Payment recording ───────────────────────────────────────────────
export const EXPENSE_PAYMENT_METHOD = { Cash: 0, BankTransfer: 1, CreditCard: 2, Check: 3 } as const;

const EXPENSE_PAYMENT_METHOD_KEYS: Record<number, string> = {
  [EXPENSE_PAYMENT_METHOD.Cash]: 'common.paymentMethods.cash',
  [EXPENSE_PAYMENT_METHOD.BankTransfer]: 'common.paymentMethods.bankTransfer',
  [EXPENSE_PAYMENT_METHOD.CreditCard]: 'common.paymentMethods.creditCard',
  [EXPENSE_PAYMENT_METHOD.Check]: 'common.paymentMethods.check',
};

/** Harcama ödeme yöntemi seçeneklerini aktif dile göre üretir. */
export function buildExpensePaymentMethodOptions(transloco: TranslocoService): { label: string; value: number }[] {
  return Object.values(EXPENSE_PAYMENT_METHOD).map((value) => ({
    label: transloco.translate(EXPENSE_PAYMENT_METHOD_KEYS[value] ?? 'common.paymentMethods.unknown'),
    value,
  }));
}

export interface SetExpensePaymentRequest {
  accountPublicId: string;
  amount: number;
  transactionDate: string; // YYYY-MM-DD
  method: number;
  referenceNo?: string;
  description?: string;
  /** Yabancı para harcamalarında ödeme günü kuru (baz para birimine çevirmek için). */
  exchangeRate?: number | null;
}
